import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';

export default function ClientPaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    // FASE 11.2 — fluxo oficial Pagar.me: a assinatura é criada no ato e a confirmação do
    // pagamento chega de forma assíncrona pelo webhook. Não há `session_id` (isso é do Stripe).
    if (location.state?.pagarme) {
      setStatus('pagarme');
      return;
    }
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/client/plans');
      return;
    }
    checkPaymentStatus(sessionId);
  }, [searchParams, location.state]);

  // FASE UX-02.1 — redirecionamento automático APÓS a lógica EXISTENTE sinalizar que a
  // assinatura foi criada/confirmada. NÃO há novo polling/endpoint/detecção: apenas um
  // atraso visual e o navigate() para o painel (o Dashboard exibe o status real).
  useEffect(() => {
    if (status === 'success' || status === 'pagarme') {
      const t = setTimeout(() => navigate('/client/dashboard'), 2000);
      return () => clearTimeout(t);
    }
  }, [status, navigate]);

  const checkPaymentStatus = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setStatus('error');
        }
      } catch (error) {
        setStatus('error');
      }
    };
    
    poll();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-white flex items-center justify-center p-4">
      <style>{`
        @keyframes jatooSlide { 0% { transform: translateX(-120%); } 100% { transform: translateX(360%); } }
        .jatoo-indeterminate { animation: jatooSlide 1.4s ease-in-out infinite; }
      `}</style>
      <Card className="w-full max-w-md rounded-3xl border-slate-200/80 shadow-2xl shadow-slate-900/5 p-8 sm:p-10 text-center">
        {status === 'checking' && (
          <>
            <div className="relative mx-auto mb-6 w-20 h-20">
              <span className="relative flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 border border-primary/15">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </span>
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-2">Verificando pagamento…</h2>
            <p className="text-sm text-slate-500 mb-6">Aguarde enquanto confirmamos seu pagamento.</p>
            <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full w-1/3 rounded-full bg-primary jatoo-indeterminate" />
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="relative mx-auto mb-6 w-20 h-20">
              <span className="absolute inset-0 rounded-full bg-emerald-500/15 animate-ping" />
              <span className="relative flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 border border-emerald-100">
                <CheckCircle className="w-11 h-11 text-emerald-500" />
              </span>
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-2">Assinatura ativada com sucesso!</h2>
            <p className="text-sm text-slate-500 mb-7 flex items-center justify-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecionando para seu painel…
            </p>
            <Button
              onClick={() => navigate('/client/dashboard')}
              className="w-full h-12 text-base font-semibold rounded-xl shadow-lg shadow-primary/25 transition-all duration-200 hover:shadow-primary/40 hover:-translate-y-0.5"
              data-testid="goto-dashboard-btn"
            >
              Ir para o Dashboard
            </Button>
          </>
        )}

        {status === 'pagarme' && (
          <>
            <div className="relative mx-auto mb-6 w-20 h-20" data-testid="pagarme-processing-icon">
              <span className="absolute inset-0 rounded-full bg-emerald-500/15 animate-ping" />
              <span className="relative flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 border border-emerald-100">
                <CheckCircle className="w-11 h-11 text-emerald-500" />
              </span>
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-2">Estamos ativando sua assinatura</h2>
            <p className="text-sm text-slate-500 leading-relaxed mb-6">
              Recebemos sua solicitação com sucesso. Estamos confirmando seu pagamento junto ao Pagar.me.
              Isso normalmente leva apenas alguns segundos.
            </p>

            <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden mb-6">
              <div className="h-full w-1/3 rounded-full bg-primary jatoo-indeterminate" />
            </div>

            <div className="rounded-2xl bg-emerald-50/70 border border-emerald-100 p-4 text-left flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-600 flex-none mt-0.5" />
              <p className="text-xs text-emerald-800 leading-relaxed">
                Pagamento processado em ambiente seguro. Sua assinatura será ativada automaticamente
                assim que o pagamento for confirmado.
              </p>
            </div>

            <Button
              onClick={() => navigate('/client/dashboard')}
              className="w-full h-12 text-base font-semibold rounded-xl mt-6 shadow-lg shadow-primary/25 transition-all duration-200 hover:shadow-primary/40 hover:-translate-y-0.5"
              data-testid="pagarme-goto-dashboard-btn"
            >
              Ir para o Dashboard
            </Button>
            <p className="mt-5 text-xs text-slate-500 flex items-center justify-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Redirecionando para seu painel…
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="relative mx-auto mb-6 w-20 h-20">
              <span className="relative flex items-center justify-center w-20 h-20 rounded-full bg-red-50 border border-red-100">
                <AlertCircle className="w-11 h-11 text-red-500" />
              </span>
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-2">Erro na verificação</h2>
            <p className="text-sm text-slate-500 mb-7">Não conseguimos confirmar seu pagamento agora. Por favor, tente novamente.</p>
            <Button
              onClick={() => navigate('/client/plans')}
              className="w-full h-12 text-base font-semibold rounded-xl transition-all duration-200 hover:-translate-y-0.5"
            >
              Voltar aos Planos
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
